import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { AlignLeft, Code2, LayoutTemplate, Trash2, Pencil, Plus, FileText, Tag } from "lucide-react";
import PlainTextEditor from "@/components/editors/PlainTextEditor";
import HtmlEditor from "@/components/editors/HtmlEditor";
import DragDropEditor from "@/components/editors/DragDropEditor";

const TYPE_META = {
  plain: { label: "Plain Text", icon: AlignLeft, color: "text-zinc-300" },
  html: { label: "HTML", icon: Code2, color: "text-amber-300" },
  builder: { label: "Drag & Drop", icon: LayoutTemplate, color: "text-[#E23A28]" },
};

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  // editing: null | { type: "plain"|"html"|"builder", initial?: tpl }
  const [editing, setEditing] = useState(null);
  const [activeTag, setActiveTag] = useState(""); // "" = all

  const load = () => {
    setLoading(true);
    api.get("/templates")
      .then((r) => setTemplates(r.data))
      .catch(() => toast.error("Failed to load templates"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const onSaved = () => load();

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await api.delete(`/templates/${t.id}`);
      toast.success("Template deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const allTags = useMemo(() => {
    const s = new Set();
    templates.forEach((t) => (t.tags || []).forEach((x) => s.add(x)));
    return Array.from(s).sort();
  }, [templates]);

  const filtered = useMemo(() => {
    if (!activeTag) return templates;
    return templates.filter((t) => (t.tags || []).includes(activeTag));
  }, [templates, activeTag]);

  // ---------------------- INLINE EDITOR VIEW ----------------------
  if (editing) {
    const close = () => setEditing(null);
    return (
      <div className="p-10 max-w-7xl cf-fade" data-testid="templates-editor-view">
        {editing.type === "plain" && (
          <PlainTextEditor initial={editing.initial} onClose={close} onSaved={onSaved} />
        )}
        {editing.type === "html" && (
          <HtmlEditor initial={editing.initial} onClose={close} onSaved={onSaved} />
        )}
        {editing.type === "builder" && (
          <DragDropEditor initial={editing.initial} onClose={close} onSaved={onSaved} />
        )}
      </div>
    );
  }

  // ---------------------- LIST VIEW ----------------------
  return (
    <div className="p-10 max-w-7xl cf-fade">
      <div className="mb-10 pb-6 border-b border-zinc-800">
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">// templates / reusable_blocks</div>
        <h1 className="font-display text-5xl font-bold uppercase mt-2 tracking-tight">Email Templates</h1>
        <p className="text-zinc-400 text-sm mt-2 max-w-2xl">
          Build reusable email templates. Tag them by persona or campaign and use them in any campaign step.
        </p>
      </div>

      {/* 3 buttons */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-800 border border-zinc-800 mb-10" data-testid="template-create-row">
        <button
          type="button"
          onClick={() => setEditing({ type: "plain" })}
          data-testid="new-plain-text-template"
          className="bg-[#0A0A0A] p-6 text-left hover:bg-zinc-900 transition-colors group"
        >
          <div className="flex items-center gap-3 mb-3">
            <AlignLeft size={20} className="text-zinc-300 group-hover:text-white" />
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">// option_01</div>
          </div>
          <div className="font-display text-xl font-bold uppercase tracking-tight text-white">Plain Text</div>
          <div className="text-sm text-zinc-400 mt-2">Simple subject + body. Best deliverability for cold outreach.</div>
          <div className="cf-btn-primary mt-5 w-fit"><Plus size={12} /> New Plain Text</div>
        </button>
        <button
          type="button"
          onClick={() => setEditing({ type: "html" })}
          data-testid="new-html-template"
          className="bg-[#0A0A0A] p-6 text-left hover:bg-zinc-900 transition-colors group"
        >
          <div className="flex items-center gap-3 mb-3">
            <Code2 size={20} className="text-amber-300 group-hover:text-white" />
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">// option_02</div>
          </div>
          <div className="font-display text-xl font-bold uppercase tracking-tight text-white">HTML</div>
          <div className="text-sm text-zinc-400 mt-2">Paste or write raw HTML with Monaco editor + live preview.</div>
          <div className="cf-btn-primary mt-5 w-fit"><Plus size={12} /> New HTML</div>
        </button>
        <button
          type="button"
          onClick={() => setEditing({ type: "builder" })}
          data-testid="new-drag-drop-template"
          className="bg-[#0A0A0A] p-6 text-left hover:bg-zinc-900 transition-colors group"
        >
          <div className="flex items-center gap-3 mb-3">
            <LayoutTemplate size={20} className="text-[#E23A28] group-hover:text-white" />
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">// option_03</div>
          </div>
          <div className="font-display text-xl font-bold uppercase tracking-tight text-white">Drag &amp; Drop</div>
          <div className="text-sm text-zinc-400 mt-2">Visual email builder — text, image, button, columns, social, video.</div>
          <div className="cf-btn-primary mt-5 w-fit"><Plus size={12} /> New Visual</div>
        </button>
      </div>

      {/* Tags / folders */}
      {allTags.length > 0 && (
        <div className="mb-6" data-testid="templates-tags-bar">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-2">
            <Tag size={11} /> Folders / tags
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setActiveTag("")}
              data-testid="tag-filter-all"
              className={`px-3 py-1 border text-xs font-mono uppercase tracking-widest transition-colors ${
                activeTag === "" ? "border-white bg-zinc-900 text-white" : "border-zinc-800 text-zinc-500 hover:text-white"
              }`}
            >
              All ({templates.length})
            </button>
            {allTags.map((t) => {
              const count = templates.filter((x) => (x.tags || []).includes(t)).length;
              const isActive = activeTag === t;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setActiveTag(t)}
                  data-testid={`tag-filter-${t}`}
                  className={`px-3 py-1 border text-xs font-mono uppercase tracking-widest transition-colors ${
                    isActive ? "border-[#E23A28] bg-zinc-900 text-[#E23A28]" : "border-zinc-800 text-zinc-500 hover:text-white"
                  }`}
                >
                  {t} ({count})
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Templates list */}
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">
        // saved_templates{activeTag && ` / ${activeTag}`}
      </div>
      {loading ? (
        <div className="text-zinc-500 text-sm">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="cf-card p-10 text-center" data-testid="templates-empty">
          <FileText size={24} className="mx-auto text-zinc-600 mb-3" />
          <div className="text-white">{activeTag ? `No templates tagged "${activeTag}"` : "No templates yet."}</div>
          <div className="text-zinc-500 text-sm mt-1">
            {activeTag ? "Try a different tag or clear the filter." : "Create your first template using one of the three editors above."}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="templates-list">
          {filtered.map((t) => {
            const meta = TYPE_META[t.type] || TYPE_META.plain;
            const Icon = meta.icon;
            return (
              <div key={t.id} className="cf-card p-5" data-testid={`template-card-${t.id}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2">
                    <Icon size={14} className={meta.color} />
                    <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{meta.label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setEditing({ type: t.type, initial: t })}
                      data-testid={`edit-template-${t.id}`}
                      className="text-zinc-500 hover:text-white p-1"
                      title="Edit"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(t)}
                      data-testid={`delete-template-${t.id}`}
                      className="text-zinc-500 hover:text-[#E23A28] p-1"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <div className="text-white font-medium truncate" data-testid={`template-name-${t.id}`}>{t.name}</div>
                <div className="text-zinc-500 text-sm mt-1 truncate">{t.subject || <em className="text-zinc-700">No subject</em>}</div>
                {(t.tags && t.tags.length > 0) && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {t.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-1.5 py-0.5 bg-zinc-900 border border-zinc-800 text-[10px] font-mono text-zinc-400"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                <div className="font-mono text-[10px] text-zinc-600 mt-3">
                  Updated {new Date(t.updated_at).toLocaleString()}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
